import React, { useState, useEffect, useRef, useCallback } from 'react';
import { collection, doc, getDoc, getDocs, query, where, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { ownerStamp } from '../recordOwner';
import { Html5Qrcode } from 'html5-qrcode';
import { Camera, CameraOff, CheckCircle2, XCircle, RotateCcw } from 'lucide-react';
import { parsePayload, verifyScan, qrDocId, REJECT, SUBJECT } from '../attendanceQr';
import { useT } from '../i18n.jsx';
import { DAY_HALF, DEFAULT_CUTOFF, halfOf, halfOfRecord, attendanceDocId } from '../attendanceDay.js';

/**
 * Scan mode -- the reverse of the QR tab.
 *
 * The QR tab puts a five-minute code on a screen and each person records their own arrival. Here a
 * member of staff holds the device and scans the card each person presents. The record is written
 * by the operator, which is the whole point: a printed card proves identity, never presence, so the
 * person vouching is the one signed in.
 *
 * That makes this the roll call it replaces, not a new kind of authority -- the same shape as typing
 * a name into the Student tab, with the name arriving by camera instead of keyboard.
 */

const SCAN_REGION = 'attendance-scan-region';
// Long enough that a card lingering in front of the lens does not fire twice, short enough that the
// next person in a queue is never made to wait.
const REPEAT_GUARD_MS = 2500;

const AttendanceScan = ({
  activeSchoolId, isMobile, schoolYear, ownEmail, ownName, canVerify, todayIso,
}) => {
  const { t } = useT();
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [last, setLast] = useState(null);
  const [log, setLog] = useState([]);
  // `${half}:${personKey}` -> the time already on the register for that half. A map rather than
  // a set because refusing a scan without saying when they already signed is unanswerable.
  const [alreadyToday, setAlreadyToday] = useState(new Map());
  const [halfCutoff, setHalfCutoff] = useState(DEFAULT_CUTOFF);
  const [loadingToday, setLoadingToday] = useState(true);

  const scannerRef = useRef(null);
  const busyRef = useRef(false);
  const seenRef = useRef(new Map());

  // Who is already on today's register. Loaded once so a second scan of the same card is refused
  // even after the operator reloads the page -- the guard cannot live only in memory, or the queue
  // that formed while the tablet was restarting all gets double-counted.
  useEffect(() => {
    if (!activeSchoolId) return;
    let cancelled = false;
    (async () => {
      setLoadingToday(true);
      try {
        const snap = await getDocs(query(
          collection(db, 'schools', activeSchoolId, 'attendance_record'),
          where('submittedDateLocal', '==', todayIso),
        ));
        const seen = new Map();
        snap.forEach(d => {
          const r = d.data() || {};
          const who = r.submittedBySid
            ? `s:${r.submittedBySid}`
            : (r.submittedByEmail ? `f:${String(r.submittedByEmail).toLowerCase()}` : '');
          if (who) seen.set(`${halfOfRecord(r)}:${who}`, r.submittedAt || '');
        });
        if (!cancelled) setAlreadyToday(seen);
      } catch (e) {
        console.error('Error loading today’s register', e);
      } finally {
        if (!cancelled) setLoadingToday(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeSchoolId, todayIso]);

  useEffect(() => {
    if (!activeSchoolId) return;
    getDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'attendance_settings'))
      .then(snap => setHalfCutoff((snap.exists() && snap.data().halfCutoff) || DEFAULT_CUTOFF))
      // A scanner that stops working at the gate is worse than one using the default boundary.
      .catch(e => console.error('Error loading attendance settings', e));
  }, [activeSchoolId]);

  const stop = useCallback(async () => {
    const s = scannerRef.current;
    scannerRef.current = null;
    setRunning(false);
    if (!s) return;
    // stop() throws synchronously when the scanner was never running -- camera denied, or a second
    // stop after an error. Swallowed, because there is nothing for the operator to do about it.
    try { await s.stop(); } catch { /* not running */ }
    try { s.clear(); } catch { /* already cleared */ }
  }, []);

  useEffect(() => () => { stop(); }, [stop]);

  const record = useCallback(async (payload, issued) => {
    const isStudent = payload.t === SUBJECT.STUDENT;
    const key = `${isStudent ? 's' : 'f'}:${isStudent ? payload.i : String(payload.i).toLowerCase()}`;
    const now = new Date();
    const half = halfOf(now.toISOString(), halfCutoff);
    const takenAt = alreadyToday.get(`${half}:${key}`);
    if (takenAt !== undefined) {
      // Refused for THIS half only -- the other mark of the day is still open.
      setLast({
        ok: false,
        name: issued.name || payload.i,
        code: 'ALREADY',
        reason: t(half === DAY_HALF.IN ? 'attendance.alreadySignedIn' : 'attendance.alreadySignedOut', {
          name: issued.name || payload.i,
          time: takenAt ? new Date(takenAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '',
        }),
        sub: issued.currentClass || '',
      });
      return;
    }

    const row = {
      ...ownerStamp(),
      // The SUBJECT of the record, matching what a hand-entered row carries -- the field is named
      // submittedBy for historical reasons but holds the person the attendance is about.
      submittedBySid: isStudent ? payload.i : '',
      submittedByEmail: isStudent ? (issued.email || '') : String(payload.i).toLowerCase(),
      submittedByInitial: isStudent ? '' : (issued.initial || ''),
      submittedByName: issued.name || '',
      submittedByRole: isStudent ? 'student' : 'staff',
      role: isStudent ? 'Student' : 'Staff',
      submittedAt: now.toISOString(),
      submittedDateLocal: todayIso,
      method: 'Card scan',
      schoolYear,
      // Who held the scanner. This is the part that carries the weight: the card said who, this
      // says who vouched for it being that person standing there.
      enteredByEmail: ownEmail,
      enteredByName: ownName,
      enteredAt: now.toISOString(),
      session: half,
      ...(canVerify ? { verified: true, verifiedByEmail: ownEmail, verifiedAt: now.toISOString() } : {}),
    };
    const id = attendanceDocId(row);
    const ref = id
      ? doc(db, 'schools', activeSchoolId, 'attendance_record', id)
      : doc(collection(db, 'schools', activeSchoolId, 'attendance_record'));
    await setDoc(ref, row);

    setAlreadyToday(prev => new Map(prev).set(`${half}:${key}`, now.toISOString()));
    setLast({ ok: true, name: issued.name || payload.i, sub: [issued.currentClass, issued.classNumber].filter(Boolean).join(' · ') });
    setLog(prev => [{ key, name: issued.name || payload.i, sub: issued.currentClass || '', at: now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) }, ...prev]);
  }, [activeSchoolId, alreadyToday, canVerify, halfCutoff, ownEmail, ownName, schoolYear, t, todayIso]);

  const onScan = useCallback(async (text) => {
    if (busyRef.current) return;
    const now = Date.now();
    const seenAt = seenRef.current.get(text);
    if (seenAt && now - seenAt < REPEAT_GUARD_MS) return;
    seenRef.current.set(text, now);

    busyRef.current = true;
    try {
      const payload = parsePayload(text);
      if (!payload) { setLast({ ok: false, code: 'UNREADABLE', reason: REJECT.UNREADABLE }); return; }

      let issued = null;
      try {
        const snap = await getDoc(doc(db, 'schools', activeSchoolId, 'attendance_qr',
          qrDocId(payload.t, payload.i)));
        issued = snap.exists() ? snap.data() : null;
      } catch (e) {
        console.error('Error verifying card', e);
        setLast({ ok: false, code: 'CARD_CHECK', reason: t('attendance.scan.cardCheckFailed') });
        return;
      }

      const check = verifyScan({ payload, issued, schoolId: activeSchoolId, todayIso });
      if (!check.ok) {
        setLast({ ok: false, name: issued?.name || '', code: check.code, reason: check.reason });
        return;
      }
      await record(payload, issued);
    } catch (e) {
      console.error('Scan failed', e);
      setLast({ ok: false, reason: e.message || 'That scan could not be recorded.' });
    } finally {
      busyRef.current = false;
    }
  }, [activeSchoolId, todayIso, record]);

  const start = async () => {
    setError('');
    try {
      const scanner = new Html5Qrcode(SCAN_REGION);
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        onScan,
        () => { /* per-frame decode misses are normal and constant */ },
      );
      setRunning(true);
    } catch (e) {
      console.error('Could not start the camera', e);
      scannerRef.current = null;
      setRunning(false);
      setError(t('attendance.scan.cameraFailed'));
    }
  };

  const card = {
    backgroundColor: 'var(--color-bg-card)',
    borderRadius: isMobile ? 0 : 'var(--radius-lg)',
    boxShadow: isMobile ? 'none' : 'var(--shadow-sm)',
    overflow: 'hidden',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ ...card, padding: isMobile ? '0.85rem' : '1.1rem 1.25rem', display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'center' }}>
        {running ? (
          <button className="btn-outline" onClick={stop} style={{ width: 'auto', padding: '0.55rem 1.1rem' }}>
            <CameraOff size={16} /> {t('attendance.scan.stop')}
          </button>
        ) : (
          <button className="btn-primary" onClick={start} style={{ width: 'auto', padding: '0.55rem 1.1rem' }}>
            <Camera size={16} /> {t('attendance.scan.start')}
          </button>
        )}
        <span style={{ fontSize: '0.83rem', color: 'var(--color-text-muted)' }}>
          {loadingToday ? t('attendance.scan.loadingRegister')
            : t('attendance.scan.alreadyOnRegister', { count: alreadyToday.size })}
        </span>
        {!canVerify && (
          // Said before the queue forms, not discovered afterwards.
          <span style={{ fontSize: '0.8rem', color: 'var(--color-warning-text)', fontWeight: '600' }}>
            {t('attendance.scan.noVerifyRights')}
          </span>
        )}
      </div>

      {error && (
        <div style={{ ...card, padding: '0.85rem 1.1rem', backgroundColor: 'var(--color-danger-bg)', color: 'var(--color-danger-text)', fontSize: '0.85rem' }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ ...card, flex: '1 1 330px', minWidth: 0, padding: isMobile ? '0.75rem' : '1rem' }}>
          <div id={SCAN_REGION} style={{ width: '100%', minHeight: running ? '260px' : '0' }} />
          {!running && (
            <div style={{ padding: '2.5rem 1rem', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: '0.88rem', lineHeight: 1.7 }}>
              {t('attendance.scan.idle')}
              <br />
              {t('attendance.scan.idleOncePerDay')}
            </div>
          )}

          {/* Big, and the only thing that changes. Somebody scanning two hundred cards reads this
              at arm's length and never looks anywhere else on the screen. */}
          {last && (
            <div style={{
              marginTop: '0.8rem', padding: '0.9rem 1rem', borderRadius: 'var(--radius-md)',
              display: 'flex', gap: '0.7rem', alignItems: 'center',
              backgroundColor: last.ok ? 'var(--color-success-bg)' : 'var(--color-danger-bg)',
              color: last.ok ? 'var(--color-success-text)' : 'var(--color-danger-text)',
            }}>
              {last.ok ? <CheckCircle2 size={26} style={{ flexShrink: 0 }} /> : <XCircle size={26} style={{ flexShrink: 0 }} />}
              {/* Room is left here for a student photo. Recognising the person is the operator's
                  job -- a lent card is a discipline matter, not something a printed code can
                  prevent -- so the platform's part is putting a face next to the name. */}
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '1.05rem', fontWeight: '700' }}>{last.name || (last.ok ? t('attendance.scan.recorded') : t('attendance.scan.notRecorded'))}</div>
                <div style={{ fontSize: '0.82rem' }}>{last.ok
                  ? (last.sub || t('attendance.scan.recorded'))
                  : (last.code ? t(`attendance.reject.${last.code.toLowerCase()}`, {}, last.reason) : last.reason)}</div>
              </div>
            </div>
          )}
        </div>

        <div style={{ ...card, flex: '1 1 260px', minWidth: 0 }}>
          <div style={{ padding: '0.55rem 0.85rem', backgroundColor: 'var(--color-bg-app)', borderBottom: '1px solid var(--color-border)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <strong style={{ fontSize: '0.82rem' }}>{t('attendance.scan.sessionLog')}</strong>
            <span style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>{log.length}</span>
            {log.length > 0 && (
              <button onClick={() => setLog([])} title={t('attendance.scan.clearLog')}
                      style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex', padding: '0.2rem' }}>
                <RotateCcw size={14} />
              </button>
            )}
          </div>
          <div style={{ maxHeight: '420px', overflowY: 'auto' }}>
            {log.length === 0 ? (
              <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: '0.83rem' }}>
                {t('attendance.scan.nothingScanned')}
              </div>
            ) : log.map(r => (
              <div key={`${r.key}-${r.at}`} style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', padding: '0.45rem 0.85rem', borderBottom: '1px solid var(--color-border)' }}>
                <CheckCircle2 size={14} style={{ color: 'var(--color-success)', flexShrink: 0 }} />
                <span style={{ flex: 1, minWidth: 0, fontSize: '0.85rem' }}>
                  {r.name}
                  {r.sub ? <span style={{ color: 'var(--color-text-muted)' }}> · {r.sub}</span> : null}
                </span>
                <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', flexShrink: 0 }}>{r.at}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceScan;
